import { VIDEO_ENSURE_IDENTITY } from 'dataSource/video/routes'

/**
 * Nest Care API via use-http. get/post resolve even on 4xx/5xx unless the
 * caller checks `error` / `response.ok` (use-http@1.x returns data.current).
 */
export type NestApi = {
  get: (url: string) => Promise<unknown>
  post: (url: string, body?: unknown) => Promise<unknown>
  /**
   * Snapshot of the last use-http exchange after get/post.
   * Required so we can reject non-OK responses that still resolve.
   */
  getLastHttpResult?: () => {
    ok: boolean
    status: number
    errorMessage?: string
  }
}

export class VideoServiceHttpError extends Error {
  status: number

  constructor(message: string, status: number) {
    super(message)
    this.name = 'VideoServiceHttpError'
    this.status = status
  }
}

export function isAuthFailure(error: unknown): boolean {
  if (error instanceof VideoServiceHttpError) {
    return error.status === 401 || error.status === 403
  }

  const message =
    error instanceof Error ? error.message.toLowerCase() : String(error)
  return (
    message.includes('sign in required') ||
    message.includes('unauthenticated') ||
    message.includes('forbidden') ||
    message.includes('video identity')
  )
}

/**
 * Reject when use-http resolved a non-OK response (or set `error`).
 * Safe no-op when `getLastHttpResult` is omitted (plain test doubles).
 */
export function assertNestHttpOk(nestApi: NestApi, data: unknown): void {
  const meta = nestApi.getLastHttpResult?.()

  if (!meta || meta.ok) {
    return
  }

  const body = data as { error?: string; message?: string } | null | undefined
  const message =
    body?.error ||
    (typeof body?.message === 'string' ? body.message : undefined) ||
    meta.errorMessage ||
    `Request failed (${meta.status || 'unknown'})`

  throw new VideoServiceHttpError(message, meta.status || 500)
}

async function nestPost(
  nestApi: NestApi,
  url: string,
  body?: unknown
): Promise<unknown> {
  const data = await nestApi.post(url, body)
  assertNestHttpOk(nestApi, data)
  return data
}

/**
 * Shared Care JWT → video-service HTTP helper (GetStream adapter).
 */
export function createVideoServiceHttp(params: {
  nestApi: NestApi
  getCareAccessToken: () => string | null | undefined
}) {
  const { nestApi, getCareAccessToken } = params
  let apiBase: string | null = null
  /** In-flight lookup only — failures clear so later calls can retry. */
  let apiBasePromise: Promise<string> | null = null
  let identityPromise: Promise<void> | null = null

  async function resolveApiBase(): Promise<string> {
    if (apiBase) {
      return apiBase
    }

    if (!apiBasePromise) {
      apiBasePromise = (async () => {
        const fromEnv = (process.env.NEXT_PUBLIC_SERVER_URL || '').replace(
          /\/$/,
          ''
        )

        if (!fromEnv) {
          throw new Error(
            'NEXT_PUBLIC_SERVER_URL is not configured for video API calls.'
          )
        }

        apiBase = fromEnv
        return apiBase
      })().then(
        (base) => base,
        (error) => {
          apiBasePromise = null
          throw error
        }
      )
    }

    return apiBasePromise
  }

  /** One in-flight / succeeded ensure; retries only after a failure. */
  async function ensureIdentity() {
    if (!identityPromise) {
      identityPromise = nestPost(nestApi, VIDEO_ENSURE_IDENTITY, {}).then(
        () => undefined,
        (error) => {
          identityPromise = null
          throw error
        }
      )
    }

    return identityPromise
  }

  async function careFetch(
    path: string,
    init?: RequestInit
  ): Promise<Response> {
    const token = getCareAccessToken()

    if (!token) {
      throw new Error('Care access token is missing.')
    }

    const base = await resolveApiBase()
    return fetch(`${base}${path}`, {
      ...init,
      headers: {
        ...(init?.headers || {}),
        Authorization: `Bearer ${token}`,
        Accept: 'application/json',
        ...(init?.body ? { 'Content-Type': 'application/json' } : {}),
      },
    })
  }

  async function careJson<T>(path: string, init?: RequestInit): Promise<T> {
    const response = await careFetch(path, init)

    if (!response.ok) {
      const body = (await response.json().catch(() => null)) as {
        error?: string
      } | null
      throw new VideoServiceHttpError(
        body?.error || `Request failed (${response.status})`,
        response.status
      )
    }

    if (response.status === 204) {
      return undefined as T
    }

    return (await response.json()) as T
  }

  return { ensureIdentity, careFetch, careJson, resolveApiBase }
}

export type VideoServiceHttp = ReturnType<typeof createVideoServiceHttp>
